<template>
  <Teleport to="#modalTarget">
    <div
      class="modal"
      v-if="showModal"
      tabindex="-1"
      aria-labelledby="basketballApiConfigModalLabel"
      aria-hidden="true"
      style="z-index: 1060"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="basketballApiConfigModalLabel">
            <font-awesome-icon icon="basketball-ball" class="me-2" />
            Basketball API-Konfiguration
          </h5>
          <button type="button" class="btn-close" @click="closeModal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div class="alert alert-info">
            <font-awesome-icon icon="info-circle" class="me-2" />
            <strong>Basketball API:</strong> Konfiguriere die API-Einstellungen für das Basketball-Template, um externe Daten abzurufen.
            <br><br>
            <strong>Automatische Aktualisierung:</strong> Die Vorlagen werden alle 3 Stunden automatisch aktualisiert.
            <br>
            <strong>Datenquelle:</strong> Spiele werden aus der API des Deutschen Basketball Bundes geladen. Bei Datenfehlern wende dich bitte direkt an den DBB.
          </div>
          <!-- Club ID Configuration -->
          <div class="row g-3">
            <div class="col-md-6">
              <label for="clubId" class="form-label">Club ID *</label>
              <input
                id="clubId"
                v-model="apiSettings.clubId"
                type="text"
                class="form-control"
                placeholder="z.B. 12345"
                required
              />
              <div class="form-text d-flex align-items-center">
                Eindeutige ID des Basketball-Clubs
                <button 
                  type="button" 
                  class="btn btn-outline-info btn-sm ms-2" 
                  @click="showClubIdHelp = true"
                  title="Hilfe zur Club ID"
                >
                  <font-awesome-icon icon="question-circle" class="me-1" />
                  Hilfe
                </button>
              </div>
            </div>
            <div class="col-md-6">
              <label for="rangeOfDays" class="form-label">Tagebereich</label>
              <input
                id="rangeOfDays"
                v-model.number="apiSettings.rangeOfDays"
                type="number"
                class="form-control"
                placeholder="z.B. 30"
                min="1"
                max="365"
              />
              <div class="form-text">Anzahl der Tage für die Datenabfrage (1-365)</div>
            </div>
          </div>

          <!-- Club Name (Readonly) -->
          <div v-if="apiSettings.clubName" class="row g-3 mt-3">
            <div class="col-md-12">
              <label for="clubName" class="form-label">
                <font-awesome-icon icon="building" class="me-2" />
                Vereinsname
              </label>
              <div class="input-group">
                <input
                  id="clubName"
                  v-model="apiSettings.clubName"
                  type="text"
                  class="form-control"
                  readonly
                />
                <span class="input-group-text">
                  <font-awesome-icon icon="check-circle" class="text-success" />
                </span>
              </div>
              <div class="form-text">
                <font-awesome-icon icon="info-circle" class="me-1" />
                Vereinsname (automatisch nach dem ersten API-Abruf gesetzt)
              </div>
            </div>
          </div>

          <!-- Boolean Settings -->
          <div class="row g-3 mt-3">
            <div class="col-md-12">
              <div class="form-check">
                <input
                  id="justHome"
                  v-model="apiSettings.justHome"
                  type="checkbox"
                  class="form-check-input"
                />
                <label for="justHome" class="form-check-label">
                  Nur Heimspiele
                </label>
                <div class="form-text">Nur Spiele zu Hause anzeigen</div>
              </div>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="mt-4 d-flex gap-2 flex-wrap">
            <button
              type="button"
              class="btn btn-primary"
              @click="saveApiSettings"
              :disabled="saving || !apiSettings.clubId"
            >
              <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
              <font-awesome-icon v-else icon="save" class="me-2" />
              {{ saving ? 'Speichern...' : 'Einstellungen speichern' }}
            </button>
            <button
              type="button"
              class="btn btn-success"
              @click="fetchApiData"
              :disabled="fetching || !hasApiSettings"
            >
              <span v-if="fetching" class="spinner-border spinner-border-sm me-2"></span>
              <font-awesome-icon v-else icon="download" class="me-2" />
              {{ fetching ? 'Laden...' : 'Daten abrufen' }}
            </button>
          </div>

          <!-- Status Messages -->
          <div v-if="statusMessage" class="mt-3">
            <div class="alert" :class="statusMessage.type === 'success' ? 'alert-success' : 'alert-danger'">
              <font-awesome-icon :icon="statusMessage.type === 'success' ? 'check-circle' : 'exclamation-triangle'" class="me-2" />
              {{ statusMessage.message }}
            </div>
          </div>

          <!-- Fetched Data Display -->
          <div v-if="fetchedData" class="mt-4">
            <h6>Abgerufene Daten:</h6>
            <div class="card">
              <div class="card-body">
                <pre class="mb-0"><code>{{ JSON.stringify(fetchedData, null, 2) }}</code></pre>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeModal">
            Schließen
          </button>
        </div>
      </div>
    </div>
  </div>

    <!-- Modal Backdrop -->
    <div v-if="showModal" class="modal-backdrop fade show"></div>

    <!-- Club ID Help Modal -->
    <div
      class="modal"
      v-if="showClubIdHelp"
      tabindex="-1"
      aria-labelledby="clubIdHelpModalLabel"
      aria-hidden="true"
      style="z-index: 1070"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="clubIdHelpModalLabel">
            <font-awesome-icon icon="question-circle" class="me-2" />
            Club ID finden - Schritt-für-Schritt Anleitung
          </h5>
          <button type="button" class="btn-close" @click="showClubIdHelp = false" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div class="alert alert-warning">
            <font-awesome-icon icon="exclamation-triangle" class="me-2" />
            <strong>Wichtig:</strong> Die Club ID findest du auf der offiziellen Website des Deutschen Basketball Bundes.
          </div>

          <div class="row">
            <div class="col-md-6">
              <h6>Schritt 1: Vereinssuche</h6>
              <p>Gehe zur <a href="https://www.basketball-bund.net/kontaktSearch.do?reqCode=search" target="_blank" rel="noopener noreferrer">Vereinssuche des DBB</a> und suche nach deinem Verein.</p>
              
              <h6>Schritt 2: Suchergebnisse</h6>
              <p>In den Suchergebnissen findest du eine Tabelle mit Vereinsnamen. Klicke auf den "S" oder "F" Button in der Spalte "Ansicht" für deinen gewünschten Verein.</p>
              
              <div class="text-center my-3">
                <img :src="stammdatenImage" alt="Suchergebnisse mit S/F Buttons" class="img-fluid border rounded" style="max-width: 100%; height: auto;">
                <div class="form-text mt-2">Suchergebnisse - Klicke auf "S" oder "F"</div>
              </div>
            </div>
            
            <div class="col-md-6">
              <h6>Schritt 3: Stammdaten öffnen</h6>
              <p>Nach dem Klick öffnet sich die Stammdaten-Seite deines Vereins.</p>
              
              <h6>Schritt 4: Club ID finden</h6>
              <p>In der URL der Stammdaten-Seite findest du die <code>object_id</code>. Diese Zahl ist deine Club ID!</p>
              
              <div class="text-center my-3">
                <img :src="clubIdImage" alt="Stammdaten-Seite mit object_id in der URL" class="img-fluid border rounded" style="max-width: 100%; height: auto;">
                <div class="form-text mt-2">Stammdaten-Seite - Die object_id in der URL ist deine Club ID</div>
              </div>
            </div>
          </div>

          <div class="alert alert-info mt-3">
            <font-awesome-icon icon="lightbulb" class="me-2" />
            <strong>Tipp:</strong> Die Club ID ist die Zahl nach <code>object_id=</code> in der URL. 
            Im Beispiel oben wäre die Club ID <strong>1281</strong>.
          </div>

          <div class="d-flex gap-2 mt-3">
            <button 
              type="button" 
              class="btn btn-primary"
              @click="openBasketballBundSearch"
            >
              <font-awesome-icon icon="external-link-alt" class="me-2" />
              Zur DBB Vereinssuche
            </button>
            <button type="button" class="btn btn-secondary" @click="showClubIdHelp = false">
              Schließen
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

    <!-- Club ID Help Modal Backdrop -->
    <div v-if="showClubIdHelp" class="modal-backdrop fade show"></div>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { showSuccess, showError } from '@/services/notification'
import { saveBasketballApiSettings, fetchBasketballApiData } from '@/api/teamsApi'
import clubIdImage from '@/assets/img/cludId.png'
import stammdatenImage from '@/assets/img/Stammdaten.png'

const props = defineProps({
  showModal: {
    type: Boolean,
    default: false,
  },
  teamTemplateId: {
    type: String,
    required: false,
    default: null,
  },
  teamTemplate: {
    type: Object,
    default: null,
  },
})

const emit = defineEmits(['modal-closed', 'settings-saved', 'data-fetched'])

// API Settings
const apiSettings = ref({
  clubId: '',
  clubName: '',
  rangeOfDays: 1,
  justHome: false
})
const saving = ref(false)
const fetching = ref(false)
const statusMessage = ref(null)
const fetchedData = ref(null)
const showClubIdHelp = ref(false)

const hasApiSettings = computed(() => {
  return apiSettings.value.clubId && apiSettings.value.rangeOfDays > 0
})

const closeModal = () => {
  emit('modal-closed')
}

const saveApiSettings = async () => {
  if (!props.teamTemplateId) {
    showError('Kein Template ausgewählt')
    return
  }

  saving.value = true
  statusMessage.value = null

  try {
    const settings = {
      clubId: apiSettings.value.clubId,
      clubName: apiSettings.value.clubName,
      rangeOfDays: apiSettings.value.rangeOfDays,
      justHome: apiSettings.value.justHome
    }

    const result = await saveBasketballApiSettings(props.teamTemplateId, settings)

    if (result.success) {
      statusMessage.value = {
        type: 'success',
        message: result.message
      }
      showSuccess(result.message)
      emit('settings-saved')
    } else {
      throw new Error(result.error)
    }
  } catch (err) {
    console.error('Error saving API settings:', err)
    statusMessage.value = {
      type: 'error',
      message: err.message || 'Fehler beim Speichern der API-Einstellungen'
    }
    showError(err.message || 'Fehler beim Speichern der API-Einstellungen')
  } finally {
    saving.value = false
  }
}

const saveClubNameOnly = async (clubName) => {
  if (!props.teamTemplateId) {
    return
  }

  try {
    const settings = {
      clubId: apiSettings.value.clubId,
      clubName: clubName,
      rangeOfDays: apiSettings.value.rangeOfDays,
      justHome: apiSettings.value.justHome
    }

    await saveBasketballApiSettings(props.teamTemplateId, settings)
  } catch (err) {
    console.error('Error saving club name:', err)
  }
}

const fetchApiData = async () => {
  if (!props.teamTemplateId) {
    showError('Kein Template ausgewählt')
    return
  }

  if (!apiSettings.value.clubId) {
    showError('Bitte gib eine Club-ID ein')
    return
  }

  if (!apiSettings.value.rangeOfDays || apiSettings.value.rangeOfDays < 1) {
    showError('Bitte gib einen gültigen Tagebereich ein')
    return
  }

  fetching.value = true
  fetchedData.value = null
  statusMessage.value = null

  try {
    // Basketball API-Daten über die zentrale API abrufen
    const result = await fetchBasketballApiData(props.teamTemplateId)
    
    if (result.success) {
      fetchedData.value = result.data
      
      // Speichere den Vereinsnamen aus den API-Daten
      if (result.data && result.data.clubName) {
        apiSettings.value.clubName = result.data.clubName
        // Speichere nur den Clubnamen, ohne die gesamten Einstellungen zu speichern
        await saveClubNameOnly(result.data.clubName)
      }
      
      statusMessage.value = {
        type: 'success',
        message: result.data?.message || result.message
      }
      showSuccess(result.data?.message || result.message)
      
      // Emit event to reload templates
      emit('data-fetched')
    } else {
      throw new Error(result.error)
    }
  } catch (err) {
    console.error('Error fetching API data:', err)
    statusMessage.value = {
      type: 'error',
      message: err.message || 'Fehler beim Abrufen der API-Daten'
    }
    showError(err.message || 'Fehler beim Abrufen der API-Daten')
  } finally {
    fetching.value = false
  }
}

const loadApiSettings = () => {
  // Lade API-Einstellungen aus der teamTemplate Prop
  if (props.teamTemplate && props.teamTemplate.settings) {
    const settings = props.teamTemplate.settings
    const apiSettingsData = settings.api

    if (apiSettingsData) {
      apiSettings.value = {
        clubId: apiSettingsData.clubId || '',
        clubName: apiSettingsData.clubName || '',
        rangeOfDays: apiSettingsData.rangeOfDays || 1,
        justHome: apiSettingsData.justHome || false
      }
    }
  }
}

const resetForm = () => {
  apiSettings.value = {
    clubId: '',
    clubName: '',
    rangeOfDays: 1,
    justHome: false
  }
  statusMessage.value = null
  fetchedData.value = null
}

watch(
  () => props.showModal,
  (newVal) => {
    if (newVal) {
      loadApiSettings()
    } else {
      resetForm()
    }
  },
)

watch(
  () => props.teamTemplate,
  () => {
    if (props.showModal) {
      loadApiSettings()
    }
  },
  { deep: true }
)

const openBasketballBundSearch = () => {
  window.open('https://www.basketball-bund.net/kontaktSearch.do?reqCode=search', '_blank', 'noopener,noreferrer')
}

onMounted(() => {
  if (props.showModal) {
    loadApiSettings()
  }
})
</script>

<style scoped>
.modal-lg {
  max-width: 800px;
}

.form-control,
.form-select {
  font-size: 0.9rem;
}

textarea.form-control {
  font-family: 'Courier New', monospace;
  font-size: 0.8rem;
}

.card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.alert {
  font-size: 0.9rem;
}

pre {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  padding: 1rem;
  max-height: 300px;
  overflow-y: auto;
}

.form-control[readonly] {
  background-color: #f8f9fa;
  border-color: #dee2e6;
  color: #6c757d;
  cursor: not-allowed;
}
</style>
